'use client';

import { useCallback } from 'react';

import { GroupEventApi } from '@ps/api/group-event';
import { SearchApi } from '@ps/api/search';
import type {
  AddGroupEventRequest,
  GroupEventSearchItem,
  UpdateGroupEventRequest,
} from '@ps/api/types';
import { useAsyncPagination } from '@ps/functions/hooks/useAsyncPagination';
import { withClientAPI } from '@ps/functions/utils/api';

// ─── Types ─────────────────────────────────────────────────────────────────

interface UseGroupEventsOptions {
  storeId: string;
  listingId: string | undefined;
  /** When true, only returns events starting from now. */
  isPublic?: boolean;
}

const PAGE_SIZE = 100;

// ─── Hook ──────────────────────────────────────────────────────────────────

export function useGroupEvents({
  storeId,
  listingId,
  isPublic,
}: UseGroupEventsOptions) {
  const loadData = useCallback(
    async (page: number, pageSize: number) => {
      if (!storeId || !listingId) {
        return { data: [] as GroupEventSearchItem[], total: 0, hasMore: false };
      }

      const api = withClientAPI(SearchApi);
      const response = await api.searchApiControllerSearchStoreGroupEvents({
        storeId,
        listingId,
        pageNo: page,
        pageSize,
        ...(isPublic && { minStartDate: new Date().toISOString() }),
      });

      const rows = response.data?.rows ?? [];
      const total = response.total ?? 0;

      return {
        data: rows,
        total,
        hasMore: page * pageSize < total,
      };
    },
    [storeId, listingId, isPublic],
  );

  const pagination = useAsyncPagination<GroupEventSearchItem>(loadData, {
    pageSize: PAGE_SIZE,
  });

  async function addEvent(request: AddGroupEventRequest): Promise<string> {
    if (!storeId || !listingId) {
      throw new Error('Missing storeId or listingId');
    }
    const api = withClientAPI(GroupEventApi);
    const response = await api.groupEventApiControllerAddGroupEvent({
      sellerId: storeId,
      AddGroupEventRequest: request,
    });
    await pagination.refresh();
    return response.id;
  }

  async function updateEvent(
    eventId: string,
    request: UpdateGroupEventRequest,
  ): Promise<void> {
    if (!storeId || !listingId) {
      throw new Error('Missing storeId or listingId');
    }
    const api = withClientAPI(GroupEventApi);
    await api.groupEventApiControllerUpdateGroupEvent({
      sellerId: storeId,
      groupEventId: eventId,
      UpdateGroupEventRequest: request,
    });
    await pagination.refresh();
  }

  async function deleteEvent(eventId: string): Promise<void> {
    if (!storeId || !listingId) {
      throw new Error('Missing storeId or listingId');
    }
    const api = withClientAPI(GroupEventApi);
    await api.groupEventApiControllerDeleteGroupEvent({
      sellerId: storeId,
      groupEventId: eventId,
    });
    await pagination.refresh();
  }

  return {
    events: pagination.data,
    isLoading: pagination.isInitializing,
    addEvent,
    updateEvent,
    deleteEvent,
    refetch: pagination.refresh,
  };
}
